import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Lock, Loader2, ShieldAlert, Mail, Key } from "lucide-react";

const GATE_STORAGE_KEY = "sonic_site_gate_v1";

export function saveGateUnlock({ token, expires_at }) {
  try {
    localStorage.setItem(
      GATE_STORAGE_KEY,
      JSON.stringify({ token, expires_at }),
    );
  } catch { /* ignore */ }
}

export function readGateUnlock() {
  try {
    const raw = localStorage.getItem(GATE_STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data?.expires_at) return null;
    if (new Date(data.expires_at).getTime() <= Date.now()) {
      localStorage.removeItem(GATE_STORAGE_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

export default function PublicGate({ onUnlocked }) {
  const [mode, setMode] = useState("email"); // "email" | "password"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (mode === "email" && !email) return;
    if (mode === "password" && !password) return;
    setSubmitting(true);
    setError("");
    try {
      const payload = mode === "email" ? { email } : { password };
      const r = await axios.post(`${API}/site/gate/unlock`, payload);
      saveGateUnlock({ token: r.data.token, expires_at: r.data.expires_at });
      onUnlocked?.();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) setError("Zu viele Versuche. Bitte in einer Stunde erneut versuchen.");
      else if (status === 401) {
        setError(mode === "email"
          ? "Diese E-Mail ist nicht freigegeben."
          : "Falsches Passwort");
      }
      else setError(err?.response?.data?.detail || "Fehler – bitte erneut versuchen.");
      if (mode === "password") setPassword("");
    } finally {
      setSubmitting(false);
    }
  };

  const switchMode = (next) => {
    setMode(next);
    setError("");
    setPassword("");
  };

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center p-4 relative overflow-hidden"
      data-testid="public-gate"
      style={{
        background:
          "radial-gradient(1200px circle at 15% -10%, #1a67ff28, transparent 60%), " +
          "radial-gradient(900px circle at 85% 110%, #0a2545, transparent 60%), " +
          "#050a1a",
      }}
    >
      {/* subtle grain */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage:
            "radial-gradient(rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "3px 3px",
        }}
      />

      <div className="relative w-full max-w-md">
        {/* Brand mark */}
        <div className="flex flex-col items-center mb-8 select-none">
          <div className="h-14 w-14 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mb-4 backdrop-blur">
            <ShieldAlert className="h-7 w-7 text-primary" strokeWidth={1.6} />
          </div>
          <div className="font-heading font-black text-2xl text-white leading-tight tracking-tight">
            Team Phoenix
          </div>
          <div className="text-[11px] font-medium uppercase tracking-[0.22em] text-white/50 mt-1">
            Team Portal · Coming Soon
          </div>
        </div>

        <Card className="border-white/10 bg-white/[0.03] backdrop-blur-md shadow-2xl">
          <div className="p-6 sm:p-8 space-y-5">
            <div className="space-y-1.5">
              <h1 className="font-heading font-bold text-lg text-white flex items-center gap-2">
                <Lock className="h-4 w-4 text-primary" />
                Zugang geschützt
              </h1>
              <p className="text-sm text-white/60 leading-relaxed">
                Diese Seite befindet sich noch im Aufbau. Freigegebene Mitglieder können sich per E-Mail eintragen – ansonsten wird ein Zugangs-Passwort benötigt.
              </p>
            </div>

            {/* Mode toggle */}
            <div className="grid grid-cols-2 gap-2 p-1 rounded-lg bg-white/5 border border-white/10">
              <button
                type="button"
                onClick={() => switchMode("email")}
                data-testid="public-gate-mode-email"
                className={`flex items-center justify-center gap-1.5 h-9 rounded-md text-xs font-semibold transition ${mode === "email" ? "bg-primary text-primary-foreground shadow" : "text-white/60 hover:text-white"}`}
              >
                <Mail className="h-3.5 w-3.5" /> E-Mail-Freigabe
              </button>
              <button
                type="button"
                onClick={() => switchMode("password")}
                data-testid="public-gate-mode-password"
                className={`flex items-center justify-center gap-1.5 h-9 rounded-md text-xs font-semibold transition ${mode === "password" ? "bg-primary text-primary-foreground shadow" : "text-white/60 hover:text-white"}`}
              >
                <Key className="h-3.5 w-3.5" /> Passwort
              </button>
            </div>

            <form onSubmit={submit} className="space-y-3">
              {mode === "email" ? (
                <Input
                  type="email"
                  placeholder="deine@email.de"
                  value={email}
                  autoFocus
                  autoComplete="email"
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={submitting}
                  data-testid="public-gate-email"
                  className="bg-white/5 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-primary/50 h-11"
                />
              ) : (
                <Input
                  type="password"
                  placeholder="Passwort"
                  value={password}
                  autoFocus
                  autoComplete="current-password"
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={submitting}
                  data-testid="public-gate-password"
                  className="bg-white/5 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-primary/50 h-11"
                />
              )}
              {error && (
                <div
                  className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-md px-3 py-2"
                  data-testid="public-gate-error"
                  role="alert"
                >
                  {error}
                </div>
              )}
              <Button
                type="submit"
                disabled={(mode === "email" ? !email : !password) || submitting}
                className="w-full h-11 font-semibold"
                data-testid="public-gate-submit"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Prüfe …</>
                ) : (
                  <>Weiter</>
                )}
              </Button>
            </form>

            <div className="pt-3 mt-3 border-t border-white/10 text-[11px] text-white/40 text-center leading-relaxed">
              Nur für autorisierte Team-Mitglieder. Alle Zugriffe werden protokolliert.
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
